import { useEffect } from 'react';
import { Provider as AppBridgeProvider } from '@shopify/app-bridge-react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppProvider } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';

// Pages
import Dashboard from './pages/Dashboard';
import LabelsList from './pages/LabelsList';
import LabelEditor from './pages/LabelEditor';
import Templates from './pages/Templates';
import Settings from './pages/Settings';
import Install from './pages/Install';

const API_KEY = import.meta.env.VITE_SHOPIFY_API_KEY || '';

function App() {
  const params = new URLSearchParams(window.location.search);
  const shop = params.get('shop') || '';
  const host = params.get('host') || '';

  // If we have shop but no host (e.g. after install or direct link), redirect into Shopify Admin
  // so the app loads in the iframe with host provided by Shopify.
  useEffect(() => {
    if (!shop || host || !API_KEY) return;
    const storeHandle = shop.replace(/\.myshopify\.com$/i, '');
    if (!storeHandle) return;
    const adminUrl = `https://admin.shopify.com/store/${storeHandle}/apps/${API_KEY}`;
    window.location.replace(adminUrl);
  }, [shop, host]);

  // Don't render App Bridge until we have host (or we're redirecting)
  if (shop && !host && API_KEY) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'sans-serif' }}>
        <p>Opening app in Shopify Admin…</p>
      </div>
    );
  }

  // App Bridge requires host when embedded; without it we show a message instead of crashing
  if (!host && API_KEY) {
    return (
      <div style={{ padding: '2rem', fontFamily: 'sans-serif', maxWidth: '400px', margin: '0 auto', textAlign: 'center' }}>
        <h1>Product Labels &amp; Badges</h1>
        <p>Open this app from your Shopify Admin: Apps → Product Labels &amp; Badges.</p>
        {shop && <p><small>Shop: {shop}</small></p>}
      </div>
    );
  }

  const config = {
    apiKey: API_KEY,
    host,
    forceRedirect: true,
  };

  return (
    <AppBridgeProvider config={config}>
      <AppProvider i18n={enTranslations}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/install" element={<Install />} />
            <Route path="/labels" element={<LabelsList />} />
            <Route path="/labels/new" element={<LabelEditor />} />
            <Route path="/labels/:id" element={<LabelEditor />} />
            <Route path="/templates" element={<Templates />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </BrowserRouter>
      </AppProvider>
    </AppBridgeProvider>
  );
}

export default App;
